import {
  AgxButton,
  AgxSelectChip,
  AgxSpacer,
  AgxTab,
  AgxTextInput,
  Icon_MagnifyingGlass,
  Icon_PlusOutline,
} from '@urbanx/agx.ui-components2'
import {
  CampaignAdminModelMarketingStatus,
  CampaignAdminModelStage,
} from '@urbanx/agx.campaigns.client'
import { AgencyAgentModel } from '@urbanx/agx.agencies.client'
import { CampaignsTab, LaunchFilterOptions } from 'types/campaigns'
import { splitPascalCase } from 'utils/stringUtils'
import { formatCampaignStageLabel } from 'utils/campaignUtils'
import { getStatusConfig } from './components/campaignMarketing/CampaignFlowStatusBadge'

interface CampaignsFilterProps {
  selectedTab: CampaignsTab
  statusFilter?: CampaignAdminModelStage[]
  agentsFilter?: string[]
  marketingFilter?: CampaignAdminModelMarketingStatus[]
  contractFilter?: string[]
  launchFilter?: LaunchFilterOptions
  staffBeginCampaignPending: boolean
  agents: AgencyAgentModel[]
  setCurrentTab: (tab: CampaignsTab) => void
  setSearchText: (text: string) => void
  setStatusFilter: (status?: CampaignAdminModelStage[]) => void
  setAgentsFilter: (agents?: string[]) => void
  setMarketingFilter: (marketing?: CampaignAdminModelMarketingStatus[]) => void
  setContractFilter: (contract?: string[]) => void
  setLaunchFilter: (launch?: LaunchFilterOptions) => void
  onNewCampaignClicked: () => void
}

export function CampaignsFilter({
  selectedTab,
  statusFilter,
  agentsFilter,
  marketingFilter,
  contractFilter,
  launchFilter,
  staffBeginCampaignPending,
  agents,
  setCurrentTab,
  setSearchText,
  setStatusFilter,
  setAgentsFilter,
  setMarketingFilter,
  setContractFilter,
  setLaunchFilter,
  onNewCampaignClicked,
}: CampaignsFilterProps) {
  return (
    <>
      <AgxSpacer>
        <AgxTab
          onChange={(selectedTab) => {
            setCurrentTab(selectedTab.id as CampaignsTab)
          }}
          options={[
            {
              id: CampaignsTab.All,
              label: 'All',
              selected: selectedTab === CampaignsTab.All,
            },
            {
              id: CampaignsTab.Archived,
              label: 'Archived',
              selected: selectedTab === CampaignsTab.Archived,
            },
          ]}
        />
      </AgxSpacer>
      <AgxSpacer
        fill
        contentJustification="end"
        contentAlignment="center"
        direction="horizontal"
        gap="xs"
      >
        <AgxTextInput
          id="campaignSearch"
          type="text"
          label=""
          onKeyUp={({ currentTarget: { value } }) => setSearchText(value)}
          noHeader
          hideOptionalText
          placeholder="Search"
          border="rounded"
          leftIcon={<Icon_MagnifyingGlass />}
        />
        <AgxSelectChip
          id="campaignStatusFilter"
          options={Object.values(CampaignAdminModelStage).map((status) => ({
            label: formatCampaignStageLabel(status),
            value: status,
            selected: statusFilter?.includes(status),
          }))}
          hideSelectAll
          variant="chip"
          type="multi-select"
          size="medium"
          align="right"
          label="Status"
          onSelect={(opt) => {
            setStatusFilter(
              opt && opt.length > 0
                ? opt.map((o) => o.value as CampaignAdminModelStage)
                : undefined
            )
          }}
          onReset={() => setStatusFilter(undefined)}
        />
        <AgxSelectChip
          id="campaignAgentFilter"
          options={
            agents?.map((agent) => ({
              label: agent.name?.fullName ?? '',
              value: agent.id ?? '',
              selected: agentsFilter?.includes(agent.id ?? '') ?? false,
            })) || []
          }
          hideSelectAll
          variant="chip"
          type="multi-select"
          size="medium"
          align="right"
          label="Agent"
          onSelect={(opt) => {
            setAgentsFilter(
              opt && opt.length > 0 ? opt.map((o) => o.value) : undefined
            )
          }}
          onReset={() => setAgentsFilter(undefined)}
        />
        <AgxSelectChip
          id="campaignMarketingFilter"
          options={Object.values(CampaignAdminModelMarketingStatus).map(
            (status) => ({
              label: getStatusConfig(status)?.text ?? splitPascalCase(status),
              value: status,
              selected: marketingFilter?.includes(status),
            })
          )}
          hideSelectAll
          variant="chip"
          type="multi-select"
          size="medium"
          align="right"
          label="Marketing"
          onSelect={(opt) => {
            setMarketingFilter(
              opt && opt.length > 0
                ? opt.map((o) => o.value as CampaignAdminModelMarketingStatus)
                : undefined
            )
          }}
          onReset={() => setMarketingFilter(undefined)}
        />
        <AgxSelectChip
          id="campaignContractFilter"
          options={[
            {
              label: 'Live',
              value: 'Live',
              selected: contractFilter?.includes('Live'),
            },
            {
              label: 'Offline',
              value: 'Offline',
              selected: contractFilter?.includes('Offline'),
            },
          ]}
          hideSelectAll
          variant="chip"
          type="multi-select"
          size="medium"
          align="right"
          label="Contract"
          onSelect={(opt) => {
            setContractFilter(
              opt && opt.length > 0 ? opt.map((o) => o.value) : undefined
            )
          }}
          onReset={() => setContractFilter(undefined)}
        />
        <AgxSelectChip
          id="campaignLaunchFilter"
          options={Object.values(LaunchFilterOptions).map((filter) => ({
            label: splitPascalCase(filter),
            value: filter,
            selected: launchFilter === filter,
          }))}
          hideSelectAll
          variant="chip"
          type="single-select"
          size="medium"
          align="right"
          label="Launch"
          onSelect={(opt) => {
            setLaunchFilter(
              opt && opt.length > 0
                ? (opt[0].value as LaunchFilterOptions)
                : undefined
            )
          }}
          onReset={() => setLaunchFilter(undefined)}
        />
        <AgxButton
          id={'btn-newCampaign'}
          text=""
          leftIcon={<Icon_PlusOutline color="var(--text-white)" />}
          size="small"
          onClick={() => onNewCampaignClicked()}
          variant="dark"
          disabled={staffBeginCampaignPending}
        />
      </AgxSpacer>
    </>
  )
}
